// filepath: frontend/src/hooks/useWebSocket.ts
import { useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { getSocket } from '@/lib/websocket';
import { useAuthStore } from '@/store/authStore';
import { useNotificationStore } from '@/store/notificationStore';
import { Notification } from '@/types';

export function useWebSocket() {
  const { accessToken } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!accessToken) return;

    const socket = getSocket(accessToken);
    socketRef.current = socket;

    socket.on('notification', (data: Notification) => {
      addNotification(data);
    });

    socket.on('connect_error', (err) => {
      console.warn('[WS] Connect error:', err.message);
    });

    return () => {
      socket.off('notification');
    };
  }, [accessToken]);

  return socketRef.current;
}

export function useShipmentSocket(
  shipmentId: string | undefined,
  handlers: Record<string, (data: unknown) => void>
) {
  const { accessToken } = useAuthStore();

  useEffect(() => {
    if (!accessToken || !shipmentId) return;

    const socket = getSocket(accessToken);

    const subscribe = () => socket.emit('subscribe:shipment', shipmentId);
    subscribe();

    // Re-subscribe after every reconnect — the server drops room membership
    // on disconnect, so we must rejoin or extraction_complete events are lost.
    socket.on('connect', subscribe);

    Object.entries(handlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });

    return () => {
      socket.off('connect', subscribe);
      socket.emit('unsubscribe:shipment', shipmentId);
      Object.keys(handlers).forEach((event) => socket.off(event));
    };
  }, [shipmentId, accessToken]);
}
